/**
 * Public-site content components (Steps, Cards, Accordion, Tabs) - markup and class names ported
 * verbatim from the Trinity_01 reference (components/docs/{Steps,Cards,Accordion,Tabs}.jsx).
 * DocContent renders these (instead of the editor-facing ones in ../) when DocVariantContext is on.
 */
import { useState, Children, cloneElement } from 'react';
import { ArrowRight, ExternalLink, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getIcon } from '../IconPicker';

// ---------------------------------------------------------------- Steps
export const Steps = ({ children, className = '' }) => {
  const steps = Children.toArray(children).filter(
    child => child?.type === Step || child?.type?.displayName === 'Step'
  );

  return (
    <div className={`steps-container my-8 relative z-10 ${className}`} data-testid="steps">
      {steps.map((child, index) => 
        cloneElement(child, {
          key: index,
          stepNumber: index + 1,
          isLast: index === steps.length - 1,
        })
      )}
    </div>
  );
};

export const Step = ({ title, children, stepNumber = 1, isLast = false, icon = null, className = '' }) => {
  const IconComponent = icon ? getIcon(icon) : null;

  return (
    <div className={`step-item relative ${className}`} data-testid={`step-${stepNumber}`}>
      <div className="flex items-center gap-4 mb-2">
        <div className="w-8 h-8 rounded-full bg-[#006BE8] flex items-center justify-center flex-shrink-0">
          {IconComponent ? (
            <IconComponent className="w-4 h-4 text-white" />
          ) : (
            <span className="text-sm font-semibold text-white">{stepNumber}</span>
          )}
        </div>
        <h4 className="text-lg font-semibold !text-gray-900 dark:!text-white">{title}</h4>
      </div>
      <div className="flex gap-4">
        <div className="w-8 flex justify-center flex-shrink-0">
          {!isLast && <div className="w-0.5 h-full bg-[#006BE8]/30 min-h-[40px]" />}
        </div>
        <div className={`flex-1 min-w-0 ${isLast ? 'pb-0' : 'pb-6'}`}>
          <div className="step-body text-[15px] !text-gray-600 dark:!text-[#999999] leading-relaxed [&>p]:mb-3 [&>p:last-child]:mb-0">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

Step.displayName = 'Step';

// ---------------------------------------------------------------- Cards
const GRID_COLS = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
};

export const Columns = ({ children, cols = 2, className = '' }) => {
  const gridCols = GRID_COLS;

  return (
    <div className={`grid gap-4 my-6 relative z-10 ${gridCols[cols] || gridCols[2]} ${className}`} data-testid="columns">
      {children}
    </div>
  );
};

export const CardGroup = ({ children, cols = 2, className = '' }) => {
  const gridCols = GRID_COLS;

  return (
    <div className={`grid gap-4 my-6 relative z-10 ${gridCols[cols] || gridCols[2]} ${className}`} data-testid="card-group">
      {children}
    </div>
  );
};

export const Card = ({ title, children, href, icon, color, className = '' }) => {
  const navigate = useNavigate();
  const isExternal = href?.startsWith('http');
  const IconComponent = icon ? getIcon(icon) : null;

  const handleClick = () => {
    if (isExternal) window.open(href, '_blank', 'noopener,noreferrer');
    else if (href) navigate(href);
  };

  const content = (
    <>
      {IconComponent && (
        <div className="mb-4">
          <IconComponent className="w-7 h-7 text-[#006BE8]" />
        </div>
      )}
      <h4 className="text-base font-semibold !text-gray-900 dark:!text-white mb-2 group-hover:text-[#006BE8] dark:group-hover:text-[#006BE8] transition-colors flex items-center gap-2">
        {title}
        {href && (
          <span className="opacity-0 group-hover:opacity-100 transition-opacity">
            {isExternal ? <ExternalLink className="w-3.5 h-3.5" /> : <ArrowRight className="w-3.5 h-3.5" />}
          </span>
        )}
      </h4>
      <div className="text-sm !text-gray-500 dark:!text-[#999999] leading-relaxed [&_p]:!text-gray-500 dark:[&_p]:!text-[#999999] [&_p]:!m-0">
        {children}
      </div>
    </>
  );

  const cardClass = `card group text-left w-full p-5 bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-white/10 hover:border-[#006BE8] dark:hover:border-[#006BE8] rounded-2xl transition-all duration-200 ${className}`;

  if (href) {
    return (
      <button onClick={handleClick} className={cardClass} data-testid="card">
        {content}
      </button>
    );
  }

  return (
    <div className={cardClass} data-testid="card">
      {content}
    </div>
  );
};

Card.displayName = 'Card';

// ---------------------------------------------------------------- Accordion
export const Accordion = ({ children, className = '' }) => {
  const items = Children.toArray(children).filter(
    child => child?.type === AccordionItem || child?.type?.displayName === 'AccordionItem'
  );

  if (items.length === 0 && children) {
    return (
      <div className={`my-6 border border-gray-200 dark:border-slate-800 rounded-lg divide-y divide-gray-200 dark:divide-slate-800 ${className}`} data-testid="accordion">
        {children}
      </div>
    );
  }

  return (
    <div className={`my-6 border border-gray-200 dark:border-slate-800 rounded-lg divide-y divide-gray-200 dark:divide-slate-800 ${className}`} data-testid="accordion">
      {items}
    </div>
  );
};

export const AccordionItem = ({ title, children, defaultOpen = false, className = '' }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={`${className}`} data-testid="accordion-item">
      <button onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3.5 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-slate-900/50 transition-colors"
        data-testid="accordion-trigger">
        <span className="!text-gray-900 dark:!text-white font-medium">{title}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 dark:text-[#999999] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-4 pb-4 [&_p]:!text-gray-600 dark:[&_p]:!text-[#999999] [&_code]:!text-[#006BE8] dark:[&_code]:!text-[#006BE8] [&_strong]:!text-gray-900 dark:[&_strong]:!text-white [&_a]:!text-[#006BE8]"
          data-testid="accordion-content">
          {children}
        </div>
      </div>
    </div>
  );
};

AccordionItem.displayName = 'AccordionItem';

// ---------------------------------------------------------------- Tabs
export const Tabs = ({ children, defaultTab = 0, className = '' }) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const tabs = Children.toArray(children).filter(
    child => child?.type === Tab || child?.type?.displayName === 'Tab'
  );

  return (
    <div className={`tabs-container my-6 ${className}`} data-testid="tabs">
      <div className="flex border-b border-gray-200 dark:border-slate-800 overflow-x-auto">
        {tabs.map((tab, index) => (
          <button key={index} onClick={() => setActiveTab(index)}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
              activeTab === index
                ? 'text-[#006BE8] border-[#006BE8]'
                : 'text-gray-400 dark:text-[#999999] border-transparent hover:text-gray-900 dark:hover:text-white hover:border-gray-300 dark:hover:border-[#787878]'
            }`}
            data-testid={`tab-${index}`}>
            {tab.props.label || `Tab ${index + 1}`}
          </button>
        ))}
      </div>
      <div className="pt-4">
        {tabs.map((tab, index) => (
          <div key={index} className={activeTab === index ? 'block' : 'hidden'} data-testid={`tab-content-${index}`}>
            {tab.props.children}
          </div>
        ))}
      </div>
    </div>
  );
};

export const Tab = ({ label, children, className = '' }) => {
  return <div className={className}>{children}</div>;
};

Tab.displayName = 'Tab';
